import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, Grid,
  CircularProgress, InputAdornment, Tooltip, IconButton, Chip,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LockIcon from "@mui/icons-material/Lock";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import { CardSchema } from "../../../schemas/CheckoutSchema";
import { PaymentService } from "../../../services/PaymentService";
import { MOP_INFO, detectCardNetwork } from "../../../constants/paymentMaps";
import { openPaymentPopup } from "../../../utils/openPaymentPopup";

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#d1d5db" },
    "&:hover fieldset": { borderColor: "#15b86d" },
    "&.Mui-focused fieldset": { borderColor: "#15b86d", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#15b86d" },
};

const CardSection = ({ paymentType, mops, txn, onPaymentInitiated }) => {
  const navigate = useNavigate();
  const [rawCardNumber, setRawCardNumber]     = useState("");
  const [displayCard, setDisplayCard]         = useState("");
  const [expiry, setExpiry]                   = useState("");
  const [detectedNetwork, setDetectedNetwork] = useState(null);
  const [networkError, setNetworkError]       = useState("");
  const [submitting, setSubmitting]           = useState(false);

  const typeLabel = paymentType === "CC" ? "Credit Card" : "Debit Card";

  const handlePaymentFailure = (message) => {
    navigate("/response-page", {
      replace: true,
      state: {
        response: {
          status:          "FAILED",
          responseMessage: message,
          txnId:           txn.transactionId,
          orderId:         txn.orderId,
          amount:          txn.amount,
        },
      },
    });
  };

  const handleCardChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 16);
    const formatted = raw.replace(/(.{4})/g, "$1 ").trim();
    setRawCardNumber(raw);
    setDisplayCard(formatted);
    setNetworkError("");

    if (raw.length >= 1) {
      const network = detectCardNetwork(raw);
      setDetectedNetwork(network);

      if (network && !mops.includes(network)) {
        // Detected a known network but it's not accepted for this payment
        setNetworkError(`${MOP_INFO[network]?.label || network} cards are not accepted for this payment`);
      } else if (!network && raw.length >= 6) {
        // 6+ digits entered and still no recognizable pattern → invalid card
        setNetworkError("Unrecognized card number — please check and re-enter");
      }
    } else {
      setDetectedNetwork(null);
    }
  };

  const handleExpiryChange = (e, setFieldValue) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 2) val = `${val.slice(0, 2)}/${val.slice(2, 4)}`;
    setExpiry(val);
    setFieldValue("expiry", val);
  };

  const handleSubmit = async (values) => {
    if (networkError) return;
    if (!rawCardNumber || rawCardNumber.length < 16) {
      toast.error("Please enter a valid 16-digit card number");
      return;
    }
    if (!expiry) {
      toast.error("Please enter the card expiry date");
      return;
    }
    const [month, yearShort] = (expiry || "/").split("/");
    setSubmitting(true);
    try {
      const res = await PaymentService.submitPayReq({
        orderId:          txn.transactionId,   // API expects transactionId here
        paymentType,
        mopType:          detectedNetwork || "",
        cardNumber:       rawCardNumber,
        cardHolderName:   values.cardName,
        cardExpiryMonth:  month || "",
        cardExpiryYear:   yearShort ? `20${yearShort}` : "",
        cardSecurity:     values.cvv,
        vpa: "",
        cardType:         detectedNetwork || "",
      });
      if (res.statusCode === 200) {
        const link = res.paymentLink || res.intentLink;
        const popup = link ? openPaymentPopup(link) : null;
        onPaymentInitiated("card", popup);
      } else {
        toast.error(res.message || "Payment request failed");
        handlePaymentFailure(res.message || "Payment request failed");
      }
    } catch (err) {
      console.error("Card payment error:", err);
      const msg = "An error occurred during payment";
      toast.error(msg);
      handlePaymentFailure(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} color="#1f2937" mb={1}>
        {typeLabel} Details
      </Typography>

      {/* Accepted networks — spotlights the detected card type */}
      <Box mb={2.5}>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Typography variant="caption" color="#6b7280" alignSelf="center">
            Accepted:
          </Typography>
          {mops.map((mop) => {
            const info    = MOP_INFO[mop];
            const isMatch = detectedNetwork === mop;
            // States:
            //  ① No detection → all full opacity (normal)
            //  ② Detected + in mops → match gets spotlight, others dim
            //  ③ Detected + NOT in mops (networkError) → all dim / greyed
            const hasDetection = !!detectedNetwork;
            const isUnsupported = hasDetection && !!networkError;

            let opacity = 1;
            if (isUnsupported)            opacity = 0.25;   // ③ all dimmed
            else if (hasDetection && !isMatch) opacity = 0.2; // ② non-matching dim

            return (
              <Box
                key={mop}
                sx={{
                  opacity,
                  transition: "opacity 0.3s ease, transform 0.2s ease",
                  transform: isMatch ? "scale(1.12)" : "scale(1)",
                  filter: isUnsupported ? "grayscale(1)" : "none",
                }}
              >
                <Chip
                  size="small"
                  variant={isMatch ? "filled" : "outlined"}
                  label={
                    info?.logo ? (
                      <Box
                        component="img"
                        src={info.logo}
                        alt={mop}
                        sx={{ height: 18, objectFit: "contain", display: "block" }}
                      />
                    ) : (info?.label || mop)
                  }
                  sx={{
                    borderColor: isMatch ? "#15b86d" : "#e5e7eb",
                    bgcolor:     isMatch ? "#f0fdf4" : "transparent",
                    height: 28,
                    boxShadow:   isMatch ? "0 0 0 2px #15b86d55" : "none",
                    ".MuiChip-label": { px: 1, display: "flex", alignItems: "center" },
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {/* Unsupported card type error banner */}
        {networkError && (
          <Box
            mt={1}
            px={1.5} py={0.75}
            sx={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              display: "flex", alignItems: "center", gap: 0.75,
            }}
          >
            <Typography variant="caption" color="#ef4444" fontWeight={600}>
              ✕ {networkError}
            </Typography>
          </Box>
        )}
      </Box>

      <Formik
        initialValues={{ cardName: "", expiry: "", cvv: "" }}
        validationSchema={CardSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              {/* Card number — controlled outside Formik */}
              <Grid item xs={12}>
                <TextField
                  label="Card Number"
                  value={displayCard}
                  onChange={handleCardChange}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 19, inputMode: "numeric" }}
                  sx={FIELD_SX}
                  error={!!networkError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCardIcon sx={{ color: "#15b86d", fontSize: "1.2rem" }} />
                      </InputAdornment>
                    ),
                    endAdornment: detectedNetwork && MOP_INFO[detectedNetwork]?.logo && (
                      <InputAdornment position="end">
                        <Box sx={{ background: "#fff", borderRadius: "6px", px: 1, py: "2px", boxShadow: "0 1px 4px rgba(0,0,0,0.12)" }}>
                          <Box
                            component="img"
                            src={MOP_INFO[detectedNetwork].logo}
                            alt={detectedNetwork}
                            sx={{ height: 20, objectFit: "contain", display: "block" }}
                          />
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Cardholder name */}
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  name="cardName"
                  label="Cardholder Name"
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                  error={touched.cardName && !!errors.cardName}
                  helperText={touched.cardName && errors.cardName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: "#15b86d", fontSize: "1.2rem" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Expiry */}
              <Grid item xs={6}>
                <TextField
                  label="Expiry (MM/YY)"
                  value={expiry}
                  onChange={(e) => handleExpiryChange(e, setFieldValue)}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 5, inputMode: "numeric" }}
                  sx={FIELD_SX}
                  error={touched.expiry && !!errors.expiry}
                  helperText={touched.expiry && errors.expiry}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ color: "#15b86d", fontSize: "1.1rem" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* CVV */}
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  name="cvv"
                  label="CVV"
                  type="password"
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 4, inputMode: "numeric" }}
                  sx={FIELD_SX}
                  error={touched.cvv && !!errors.cvv}
                  helperText={touched.cvv && errors.cvv}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#15b86d", fontSize: "1.1rem" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="3-digit code on the back of your card (4 digits for Amex)" arrow>
                          <IconButton size="small" tabIndex={-1}>
                            <HelpOutlineIcon fontSize="small" sx={{ color: "#9ca3af" }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Submit */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting || !!networkError}
                  sx={{
                    py: 1.5, fontWeight: 600, borderRadius: "10px", fontSize: "1rem",
                    textTransform: "none",
                    background: "#15b86d",
                    "&:hover": { background: "#13a360" },
                    "&:disabled": { background: "#e5e7eb", color: "#9ca3af" },
                  }}
                >
                  {submitting
                    ? <CircularProgress size={22} sx={{ color: "white" }} />
                    : `Pay ₹${parseFloat(txn.amount || 0).toFixed(2)}`}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CardSection;
